import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useLanguage } from '../../../contexts/LanguageContext';

const PROPERTY_TYPES = ['House', 'Apartment', 'Townhouse', 'Villa', 'Land', 'Commercial', 'Other'];
const REGIONS = ['Australia', 'UAE'];

const ManualPropertyForm = ({ onAnalyze, isLoading }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    address: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    propertyType: 'House',
    region: 'Australia',
    description: ''
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData?.address?.trim()) newErrors.address = 'Property address is required';
    if (!formData?.price?.trim()) newErrors.price = 'Asking price is required';
    return newErrors;
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e?.target?.value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    onAnalyze({ type: 'manual', value: formData });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Input
            label={`${t('propertyAddress')} *`}
            placeholder={t('propertyAddressPlaceholder')}
            value={formData?.address}
            onChange={handleChange('address')}
            error={errors?.address}
          />
        </div>
        <Input
          label={`${t('askingPrice')} *`}
          placeholder={t('askingPricePlaceholder')}
          value={formData?.price}
          onChange={handleChange('price')}
          error={errors?.price}
        />
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('propertyType')}</label>
          <select
            value={formData?.propertyType}
            onChange={handleChange('propertyType')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            {PROPERTY_TYPES?.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <Input
          label={t('bedrooms')}
          type="number"
          placeholder="e.g. 3"
          value={formData?.bedrooms}
          onChange={handleChange('bedrooms')}
          min="0"
        />
        <Input
          label={t('bathrooms')}
          type="number"
          placeholder="e.g. 2"
          value={formData?.bathrooms}
          onChange={handleChange('bathrooms')}
          min="0"
        />
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('region')}</label>
          <select
            value={formData?.region}
            onChange={handleChange('region')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            {REGIONS?.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('propertyDescription')}</label>
          <textarea
            value={formData?.description}
            onChange={handleChange('description')}
            placeholder={t('propertyDescriptionPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
          />
        </div>
      </div>
      <Button
        variant="default"
        size="lg"
        iconName="Sparkles"
        iconPosition="left"
        onClick={handleSubmit}
        fullWidth
        disabled={isLoading}
      >
        {isLoading ? t('analyzing') : t('analyzeProperty')}
      </Button>
    </div>
  );
};

export default ManualPropertyForm;
